import {
    type ChainPairTypeTaproot,
    type LockupTransaction,
    getChainSwapTransactions,
    getReverseTransaction,
    postChainSwapDetails,
} from "boltz-swaps/client";
import { SwapType } from "boltz-swaps/types";
import { getOutputAmount, getTransaction } from "boltz-swaps/utxo";
import log from "loglevel";
import {
    type Accessor,
    type JSX,
    type Setter,
    createContext,
    createEffect,
    createSignal,
    on,
    useContext,
} from "solid-js";

import { hiddenInformation } from "../components/settings/PrivacyMode";
import { BTC, LBTC } from "../consts/Assets";
import { swapStatusPending, swapStatusSuccess } from "../consts/SwapStatus";
import { createSwapModifier } from "../hooks/useModifySwap";
import { getTransactionOutSpend } from "../utils/blockchain";
import {
    isPositivePersistedAmount,
    withChainSwapQuoteLock,
} from "../utils/chainSwapQuote";
import {
    claim,
    createSubmarineSignature,
    createTheirPartialChainSwapSignature,
    findSwapOutputVout,
} from "../utils/claim";
import { decodeAddress, findOutputByScript } from "../utils/compat";
import { formatError } from "../utils/errors";
import { getPair, parseBlindingKey } from "../utils/helper";
import { isSwapClaimable } from "../utils/rescue";
import {
    type ChainSwap,
    type ReverseSwap,
    type SomeSwap,
    type SubmarineSwap,
    isEvmSwap,
} from "../utils/swapCreator";
import { useGlobalContext } from "./Global";

type SwapStatus = {
    id: string;
    status: string;

    failureReason?: string;
    transaction?: SwapStatusTransaction;
};

export type PayContextType = {
    failureReason: Accessor<string>;
    setFailureReason: Setter<string>;
    swap: Accessor<SomeSwap | null>;
    setSwap: Setter<SomeSwap | null>;
    swapStatus: Accessor<string>;
    setSwapStatus: Setter<string>;
    swapStatusTransaction: Accessor<SwapStatusTransaction>;
    setSwapStatusTransaction: Setter<SwapStatusTransaction>;
    refundableUTXOs: Accessor<
        (Partial<LockupTransaction> & Pick<LockupTransaction, "hex">)[]
    >;
    setRefundableUTXOs: Setter<
        (Partial<LockupTransaction> & Pick<LockupTransaction, "hex">)[]
    >;
    shouldIgnoreBackendStatus: Accessor<boolean>;
    setShouldIgnoreBackendStatus: Setter<boolean>;
    claimSwap: (swapId: string, data: SwapStatus) => Promise<void>;
    isSwapClaiming: (swapId: string) => boolean;
};

const PayContext = createContext<PayContextType>();

type SwapStatusTransaction = {
    hex?: string;
    id?: string;
};

const coopClaimableSymbols = [BTC, LBTC];

const PayProvider = (props: { children: JSX.Element }) => {
    const {
        t,
        deriveKey,
        getSwap,
        privacyMode,
        notify,
        fetchPairs,
        pairs,
        modifySwapStorage,
        zeroConf,
        getSwaps,
    } = useGlobalContext();
    const [failureReason, setFailureReason] = createSignal<string>("");
    const [swap, setSwap] = createSignal<SomeSwap | null>(null, {
        // To allow updating properties of the swap object without replacing it completely
        equals: () => false,
    });

    // Locked read-modify-write of a stored swap that also keeps the displayed
    // swap signal in sync. PayProvider cannot call usePayContext on itself, so
    // it builds the helper directly from the factory.
    const modifySwap = createSwapModifier(modifySwapStorage, swap, setSwap);
    const [swapStatus, setSwapStatus] = createSignal<string>("");
    const [swapStatusTransaction, setSwapStatusTransaction] =
        createSignal<SwapStatusTransaction>({});
    const [refundableUTXOs, setRefundableUTXOs] = createSignal<
        (Partial<LockupTransaction> & Pick<LockupTransaction, "hex">)[]
    >([]);
    const [shouldIgnoreBackendStatus, setShouldIgnoreBackendStatus] =
        createSignal<boolean>(false);

    const helpServerClaim = async (swap: ChainSwap) => {
        if (swap.claimTx === undefined) {
            log.warn(
                `Not helping server claim Chain Swap ${swap.id} because we have not claimed yet`,
            );
            return;
        }

        try {
            log.debug(
                `Helping server claim ${swap.assetSend} of Chain Swap ${swap.id}`,
            );
            const sig = await createTheirPartialChainSwapSignature(
                deriveKey,
                swap,
            );
            if (sig === undefined) {
                return;
            }
            await postChainSwapDetails(swap.id, undefined, sig);
        } catch (e) {
            log.warn(
                `Helping server claim Chain Swap ${swap.id} failed: ${formatError(e)}`,
            );
        }
    };

    const pendingServerClaimHelp = new Set<string>();
    const helpingServerClaims = new Set<string>();
    const maybeHelpServerClaim = async (chainSwap: ChainSwap) => {
        if (!pendingServerClaimHelp.has(chainSwap.id)) {
            return;
        }

        if (helpingServerClaims.has(chainSwap.id)) {
            return;
        }

        if (chainSwap.claimTx === undefined) {
            log.debug(
                `Deferred server claim help for Chain Swap ${chainSwap.id} is still waiting for claimTx`,
            );
            return;
        }

        helpingServerClaims.add(chainSwap.id);
        try {
            log.info(
                `Retrying deferred server claim help for Chain Swap ${chainSwap.id}`,
            );
            await helpServerClaim(chainSwap);
        } finally {
            pendingServerClaimHelp.delete(chainSwap.id);
            helpingServerClaims.delete(chainSwap.id);
        }
    };

    const recoverChainSwapAmounts = async (
        swap: ChainSwap,
        lockupTxHex: string,
    ): Promise<ChainSwap> => {
        const getClaimFee = () =>
            getPair<ChainPairTypeTaproot>(
                pairs(),
                SwapType.Chain,
                swap.assetSend,
                swap.assetReceive,
            )?.fees.minerFees.user.claim;

        let claimFee = getClaimFee();
        if (claimFee === undefined) {
            await fetchPairs();
            claimFee = getClaimFee();
        }
        if (claimFee === undefined) {
            throw new Error(`claim fee is unavailable for swap ${swap.id}`);
        }

        const output = findOutputByScript(
            swap.assetReceive,
            getTransaction(swap.assetReceive).fromHex(lockupTxHex),
            decodeAddress(swap.assetReceive, swap.claimDetails.lockupAddress)
                .script,
        );
        if (output === undefined) {
            throw new Error(
                `server lockup output is missing for swap ${swap.id}`,
            );
        }

        const lockupAmount = await getOutputAmount(swap.assetReceive, {
            ...output,
            blindingPrivateKey: parseBlindingKey(swap, false),
        } as never);
        const receiveAmount = lockupAmount - claimFee;
        if (receiveAmount <= 0) {
            throw new Error(
                `recovered receive amount is not positive for swap ${swap.id}`,
            );
        }

        log.info(
            `Recovered receive amount ${receiveAmount} for swap ${swap.id} from its server lockup`,
        );
        const recovered = await modifySwap<ChainSwap>(swap.id, (s) => {
            s.receiveAmount = receiveAmount;
            s.claimDetails.amount = lockupAmount;
        });
        if (recovered === null) {
            throw new Error(`swap ${swap.id} is missing from storage`);
        }

        return recovered;
    };

    const [claimingSwaps, setClaimingSwaps] = createSignal(new Set<string>(), {
        equals: false,
    });
    const isSwapClaiming = (swapId: string) => claimingSwaps().has(swapId);
    const claimSwap = async (swapId: string, data: SwapStatus) => {
        if (claimingSwaps().has(swapId)) {
            return;
        }

        const currentSwap = await getSwap(swapId);
        if (currentSwap === null) {
            log.warn(`claimSwap: swap ${swapId} not found`);
            return;
        }

        if (
            currentSwap.type === SwapType.Chain &&
            data.status === swapStatusPending.TransactionClaimPending &&
            coopClaimableSymbols.includes((currentSwap as ChainSwap).assetSend)
        ) {
            const chainSwap = currentSwap as ChainSwap;
            pendingServerClaimHelp.add(chainSwap.id);

            if (chainSwap.claimTx === undefined) {
                log.info(
                    `Deferring server claim help for Chain Swap ${chainSwap.id} until claimTx is known`,
                );
                return;
            }

            await maybeHelpServerClaim(chainSwap);
            return;
        }

        if (isEvmSwap(currentSwap)) {
            if (
                data.status === swapStatusPending.TransactionMempool &&
                data.transaction !== undefined
            ) {
                const lockupTxId = data.transaction.id;
                if (lockupTxId === undefined) {
                    return;
                }

                await modifySwap(swapId, (s) => {
                    s.lockupTx = lockupTxId;
                });
            }

            return;
        }

        if (
            (currentSwap.type === SwapType.Reverse &&
                zeroConf() &&
                data.status === swapStatusPending.TransactionMempool) || // necessary for the autoclaim when zeroConf is toggled with a pending swap
            data.status === swapStatusSuccess.InvoiceSettled
        ) {
            data.transaction = await getReverseTransaction(currentSwap.id);
        } else if (
            currentSwap.type === SwapType.Chain &&
            (data.status === swapStatusSuccess.TransactionClaimed ||
                (zeroConf() &&
                    data.status === swapStatusPending.TransactionServerMempool)) // necessary for the autoclaim when zeroConf is toggled with a pending swap
        ) {
            data.transaction = (
                await getChainSwapTransactions(currentSwap.id)
            ).serverLock.transaction;
        }

        if (
            currentSwap.claimTx === undefined &&
            data.transaction !== undefined &&
            isSwapClaimable({
                status: data.status,
                type: currentSwap.type,
                includeSuccess: true,
                zeroConf: zeroConf(),
            })
        ) {
            try {
                setClaimingSwaps((swaps) => {
                    swaps.add(swapId);
                    return swaps;
                });

                const transaction = data.transaction as { hex: string };
                const res = await withChainSwapQuoteLock(swapId, async () => {
                    let claimableSwap = await getSwap<ReverseSwap | ChainSwap>(
                        swapId,
                    );
                    if (
                        claimableSwap === null ||
                        claimableSwap.claimTx !== undefined
                    ) {
                        return undefined;
                    }
                    if (
                        !isPositivePersistedAmount(claimableSwap.receiveAmount)
                    ) {
                        if (claimableSwap.type !== SwapType.Chain) {
                            throw new Error(
                                `swap ${swapId} has an invalid persisted receive amount`,
                            );
                        }
                        claimableSwap = await recoverChainSwapAmounts(
                            claimableSwap as ChainSwap,
                            transaction.hex,
                        );
                    }

                    const result = await claim(
                        deriveKey,
                        claimableSwap,
                        transaction,
                        true,
                    );
                    if (result === undefined) {
                        return undefined;
                    }
                    const claimedSwap = await modifySwap(result.id, (s) => {
                        s.claimTx = result.claimTx;
                    });
                    return claimedSwap === null ? undefined : result;
                });
                if (res === undefined) {
                    return;
                }

                notify(
                    "success",
                    t("swap_completed", {
                        id: privacyMode() ? hiddenInformation : res.id,
                    }),
                );
            } catch (e) {
                if (
                    typeof e === "string" &&
                    (e.includes("bad-txns-inputs-missingorspent") ||
                        e.includes("Transaction outputs already in utxo set") ||
                        e.includes("Inputs missing or spent"))
                ) {
                    if (
                        data.transaction === undefined ||
                        data.transaction.hex === undefined
                    ) {
                        return;
                    }
                    const lockupTx = getTransaction(
                        currentSwap.assetReceive,
                    ).fromHex(data.transaction.hex);
                    const vout = findSwapOutputVout(
                        deriveKey,
                        currentSwap as ReverseSwap | ChainSwap,
                        lockupTx,
                    );

                    if (vout !== undefined) {
                        try {
                            log.debug(
                                `swap ${currentSwap.id}: checking for spent status of tx output ${data.transaction.id}:${vout}`,
                            );
                            if (data.transaction.id === undefined) {
                                return;
                            }
                            const outspend = await getTransactionOutSpend(
                                currentSwap.assetReceive,
                                data.transaction.id,
                                vout,
                            );

                            if (outspend?.spent && outspend.txid) {
                                log.debug(
                                    `swap ${currentSwap.id}: lockup tx was already claimed, updating claimTx id to ${outspend.txid}`,
                                );
                                const claimTxId = outspend.txid;
                                await modifySwap(currentSwap.id, (s) => {
                                    s.claimTx = claimTxId;
                                });

                                return;
                            }
                            log.debug(
                                `got bad-txns-inputs-missingorspent when claiming swap ${currentSwap.id} but tx ${data.transaction.id} is not spent`,
                            );
                        } catch (e) {
                            log.error(
                                `could not check if lockup tx from swap ${currentSwap.id} is spent: ${formatError(e)}`,
                            );
                        }
                    }
                }

                const msg = t("claim_fail", {
                    id: privacyMode() ? hiddenInformation : currentSwap.id,
                });
                log.error(msg, e);
                notify("error", msg);
            } finally {
                setClaimingSwaps((swaps) => {
                    swaps.delete(swapId);
                    return swaps;
                });
            }
        } else if (
            currentSwap.type === SwapType.Submarine &&
            data.status === swapStatusPending.TransactionClaimPending &&
            currentSwap.receiveAmount >=
                (getPair(
                    pairs(),
                    currentSwap.type,
                    currentSwap.assetSend,
                    currentSwap.assetReceive,
                )?.limits.minimal ?? Number.MAX_SAFE_INTEGER)
        ) {
            try {
                await createSubmarineSignature(
                    deriveKey,
                    currentSwap as SubmarineSwap,
                );
                notify(
                    "success",
                    t("swap_completed", {
                        id: privacyMode() ? hiddenInformation : currentSwap.id,
                    }),
                );
            } catch (e) {
                if (e === "swap not eligible for a cooperative claim") {
                    log.debug(
                        `Server did not want help claiming ${currentSwap.id}`,
                    );
                    return;
                }

                const msg =
                    "creating cooperative signature for submarine swap claim failed";
                log.warn(msg, e);
                notify("error", msg);
            }
        }
    };

    createEffect(
        on([zeroConf], async () => {
            if (!zeroConf()) {
                return;
            }

            // Attempt to claim eligible swaps when zeroConf changes to true
            const swaps = await getSwaps();
            for (const swap of swaps) {
                if (swap.status === undefined) {
                    continue;
                }
                if (
                    isSwapClaimable({
                        status: swap.status,
                        type: swap.type,
                        zeroConf: zeroConf(),
                        swap,
                    })
                ) {
                    try {
                        await claimSwap(swap.id, {
                            id: swap.id,
                            status: swap.status,
                        });
                    } catch (e) {
                        log.warn(
                            `Error claiming swap ${swap.id}: ${formatError(e)}`,
                        );
                        continue;
                    }
                }
            }
        }),
    );

    createEffect(
        on([swap, swapStatus], ([currentSwap, currentStatus]) => {
            if (
                currentSwap === null ||
                currentSwap.type !== SwapType.Chain ||
                currentStatus !== swapStatusPending.TransactionClaimPending
            ) {
                return;
            }

            void maybeHelpServerClaim(currentSwap as ChainSwap);
        }),
    );

    return (
        <PayContext.Provider
            value={{
                failureReason,
                setFailureReason,
                swap,
                setSwap,
                swapStatus,
                setSwapStatus,
                swapStatusTransaction,
                setSwapStatusTransaction,
                refundableUTXOs,
                setRefundableUTXOs,
                shouldIgnoreBackendStatus,
                setShouldIgnoreBackendStatus,
                claimSwap,
                isSwapClaiming,
            }}>
            {props.children}
        </PayContext.Provider>
    );
};

const usePayContext = () => {
    const context = useContext(PayContext);
    if (!context) {
        throw new Error("usePayContext: cannot find a PayContext");
    }
    return context;
};

export { usePayContext, PayProvider, SwapStatusTransaction };
